import { Box, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';

// COMPONENTS
import TariffingList from '../../components/admin/tariffing/TariffingTable';
import TariffingSingleView from '../../components/admin/tariffing/TariffingSingleView';
import Layout from '../../components/layouts/Layout';
import useTariffRequest from '../../requests/TariffRequest';

import { AdminRoutes } from '../../routes/AdminRoutes';

const Tariff = ({
  customer,
  location,
  errorHandler,
  accessLevel,
  accessType,
}) => {
  const [tariffSchemes, setTariffSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [addNewTariffScheme, setAddNewTariffScheme] = useState(false);
  const [showTariffPlan, setShowTariffPlan] = useState(false);
  const [showTariffLine, setShowTariffLine] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [addNewTariffPlan, setAddNewTariffPlan] = useState(false);
  const [addNewTariffLine, setAddNewTariffLine] = useState(false);
  const { getTariffSchemes } = useTariffRequest();

  useEffect(() => {
    const fetchInitialData = async () => {
      if (customer) {
        const schemes = await handleGetTariffSchemes();
        setTariffSchemes(schemes || []);

        if (location?.state?.tariffPlanId && location?.state?.tariffSchemeId) {
          const { tariffPlanId, tariffSchemeId } = location.state;
          const selectedScheme = schemes?.find(
            (e) => e.tariff_scheme_id === tariffSchemeId
          );
          if (selectedScheme) {
            setSelectedScheme(selectedScheme);
            setSelectedPlanId(tariffPlanId);
            setShowTariffPlan(true);
          }
        }
      }
    };

    fetchInitialData();
  }, [customer, location]);

  const handleBackClick = async (
    refreshFlag,
    showTariffPlanFlag = false,
    showTariffLineFlag = false
  ) => {
    setAddNewTariffScheme(false);

    // Always check editing state first
    if (isEditing) {
      setIsEditing(false);
      return;
    }

    // First handle any explicit navigation flags
    if (showTariffLineFlag) {
      setShowTariffLine(true);
      setShowTariffPlan(false);
      setIsEditing(false);
      setAddNewTariffPlan(false);
      setAddNewTariffLine(false);
      return;
    }

    if (showTariffPlanFlag) {
      setShowTariffLine(false);
      setShowTariffPlan(true);
      setIsEditing(false);
      setAddNewTariffPlan(false);
      setAddNewTariffLine(false);
      return;
    }

    // Then handle current state navigation
    if (showTariffLine) {
      console.log('show line');
      setShowTariffLine(false);
      setShowTariffPlan(true);
      setIsEditing(false);
      setAddNewTariffLine(false);
      return;
    }

    if (showTariffPlan) {
      setShowTariffLine(false);
      setShowTariffPlan(false);
      setSelectedPlanId(null);
      setIsEditing(false);
      setAddNewTariffPlan(false);
      return;
    }

    // Default back behavior
    if (refreshFlag) {
      const schemes = await getTariffSchemes(errorHandler);
      setTariffSchemes(schemes || []);
    }

    // Reset all states
    setSelectedScheme(null);
    setSelectedPlanId(null);
    setAddNewTariffScheme(false);
    setAddNewTariffPlan(false);
    setShowTariffPlan(false);
    setShowTariffLine(false);
    setIsEditing(false);
  };

  const handleAddNewTariffScheme = () => {
    setAddNewTariffScheme(true);
    // setIsEditing(true);
  };

  const handleGetTariffSchemes = async () => {
    try {
      const schemes = await getTariffSchemes(errorHandler);
      return schemes;
    } catch (error) {
      console.error('Error fetching tariff schemes:', error);
      return [];
    }
  };

  const showSingleScheme = (scheme) => {
    setSelectedScheme(scheme);
  };

  const sortListForTable = () => {
    if (!Array.isArray(tariffSchemes)) {
      console.warn('tariffSchemes is not an array:', tariffSchemes);
      return [];
    }

    return tariffSchemes.sort((a, b) =>
      a.tariff_scheme_name.localeCompare(b.tariff_scheme_name)
    );
  };

  const getPageTitle = () => {
    if (showTariffLine) {
      return 'Tariff Line Information';
    }
    if (showTariffPlan) {
      return 'Tariff Plan Information';
    }
    if (selectedScheme) {
      return 'Tariff Scheme Information';
    }
    if (addNewTariffScheme) {
      return 'New Tariff Scheme';
    }
    return 'List of Tariff Schemes';
  };

  const generateContent = () => {
    if (showTariffLine) {
      return (
        <TariffingSingleView
          tariffScheme={selectedScheme}
          selectedPlanId={selectedPlanId}
          isNew={false}
          backButtonClick={handleBackClick}
          errorHandler={errorHandler}
          showTariffLine={true}
          setShowTariffLine={setShowTariffLine}
          setShowTariffPlan={setShowTariffPlan}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          addNewTariffLine={addNewTariffLine}
          setAddNewTariffLine={setAddNewTariffLine}
        />
      );
    }
    if (showTariffPlan) {
      return (
        <TariffingSingleView
          tariffScheme={selectedScheme}
          selectedPlanId={selectedPlanId}
          isNew={false}
          addNewTariffPlan={addNewTariffPlan}
          setAddNewTariffPlan={setAddNewTariffPlan}
          backButtonClick={handleBackClick}
          errorHandler={errorHandler}
          showTariffPlan={true}
          setShowTariffLine={setShowTariffLine}
          setShowTariffPlan={setShowTariffPlan}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          addNewTariffLine={addNewTariffLine}
          setAddNewTariffLine={setAddNewTariffLine}
        />
      );
    }
    if (selectedScheme || addNewTariffScheme) {
      return (
        <TariffingSingleView
          tariffScheme={selectedScheme}
          selectedPlanId={null}
          isNew={addNewTariffScheme}
          addNewTariffPlan={addNewTariffPlan}
          setAddNewTariffPlan={setAddNewTariffPlan}
          backButtonClick={handleBackClick}
          errorHandler={errorHandler}
          setShowTariffLine={setShowTariffLine}
          setShowTariffPlan={setShowTariffPlan}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          addNewTariffLine={addNewTariffLine}
          setAddNewTariffLine={setAddNewTariffLine}
        />
      );
    }
    return (
      <TariffingList
        tariffSchemes={sortListForTable()}
        addNewTariffScheme={handleAddNewTariffScheme}
        showSingleTariffScheme={showSingleScheme}
        errorHandler={errorHandler}
      />
    );
  };

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        title={getPageTitle()}
        routes={AdminRoutes}
        accessLevel={accessLevel}
        accessType={accessType}
        backButtonText={
          selectedScheme ||
          addNewTariffScheme ||
          showTariffPlan ||
          showTariffLine
            ? 'Back'
            : null
        }
        handleBackButton={handleBackClick}
        titleRowRightComponent={
          !selectedScheme && !addNewTariffScheme && !showTariffPlan ? (
            <Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleAddNewTariffScheme}
              >
                Add New
              </Button>
            </Box>
          ) : null
        }
      >
        {generateContent()}
      </Layout>
    );
  }
  return null;
};

export default Tariff;
